import { ScannedWorkout } from '@cf-shared/scan-workout-plan-interface';

// Baked REAL scan results for the test-mode MOCK path (and the mock-based E2E
// scenario): captured from the owner's live backend-STOP testing on 2026-06-11
// (#3654) against the final prompt. Program 1 is a 3-day MacroFactor program
// scanned in day-batches (the batch+append flow); Program 2 is a 2-page
// handwritten program scanned in one call. Cosmetic name annotations from the
// test helper are stripped — this is exactly the post-mapping server payload
// the app receives. Regenerate by re-running the scans with
// tmp/scan-workout-plan-local.cjs and re-baking.

export interface MockScannedProgram {
  /** menu label on the dev MOCK picker */
  label: string;
  workout: ScannedWorkout;
}

export const MOCK_SCANNED_PROGRAMS: MockScannedProgram[] = [
  {
    label: 'Program 1',
    workout: {
      workouts: [
        {
          name: 'Workout A',
          exercises: [
            {
              name: 'Low Incline Smith Machine Press',
              matchedExerciseId: null,
              sets: 4,
              targetReps: 5,
              suggestedReplacementId: 'exercise/$15_incline_dumbbell_bench_press',
            },
            {
              name: 'Bent-Over Barbell Row',
              matchedExerciseId: 'exercise/$bent_over_barbell_row',
              sets: 4,
              targetReps: 12,
            },
            {
              name: 'Plate-Loaded Machine Shoulder Press',
              matchedExerciseId: null,
              sets: 4,
              targetReps: 11,
              suggestedReplacementId: 'exercise/$dumbell_overhead_press',
            },
            {
              name: '45° Leg Press',
              matchedExerciseId: 'exercise/$leg_press',
              sets: 4,
              targetReps: 11,
            },
            {
              name: 'Pin-Loaded Machine Preacher Curl',
              matchedExerciseId: 'exercise/$preacher_scott_curl',
              sets: 4,
              targetReps: 10,
            },
            {
              name: 'Pec Deck Fly',
              matchedExerciseId: 'exercise/$pec_deck_fly',
              sets: 4,
              targetReps: 10,
            },
            {
              name: 'Single Arm Cable Rear Delt Fly',
              matchedExerciseId: 'exercise/$bayesian_reverse_fly',
              sets: 4,
              targetReps: 10,
            },
            {
              name: 'Seated Single Leg Hamstring Curl',
              matchedExerciseId: 'exercise/$unilateral_leg_curl',
              sets: 4,
              targetReps: 10,
            },
            {
              name: 'Side-Lying Weighted Hip Abduction Leg Raise',
              matchedExerciseId: null,
              sets: 4,
              targetReps: 10,
              suggestedReplacementId: 'exercise/$dumbbell_lying_hip_abduction',
            },
            {
              name: 'Nordic Hamstring Curl',
              matchedExerciseId: 'exercise/$nordic_hamstring_curl',
              sets: 4,
              targetReps: 10,
            },
            {
              name: 'Glute Ham Developer Sit-Up',
              matchedExerciseId: null,
              sets: 3,
              targetReps: 10,
              suggestedReplacementId: 'exercise/$ab_machine_crunch',
            },
          ],
        },
        {
          name: 'Workout B',
          exercises: [
            {
              name: 'Seated Neutral Grip Dumbbell Overhead Press',
              matchedExerciseId: 'exercise/$dumbell_overhead_press',
              sets: 4,
              targetReps: 5,
            },
            {
              name: 'Neutral Grip Machine-Assisted Pull-Up',
              matchedExerciseId: null,
              sets: 4,
              targetReps: 12,
              suggestedReplacementId: 'exercise/$pull_up',
            },
            {
              name: 'Single Arm Elbow-In Dumbbell Row',
              matchedExerciseId: 'exercise/$dumbbell_row',
              sets: 4,
              targetReps: 8,
            },
            {
              name: 'Smith Machine Good Morning',
              matchedExerciseId: null,
              sets: 4,
              targetReps: 8,
              suggestedReplacementId: 'exercise/$goodmorning',
            },
            {
              name: 'Pin-Loaded Machine Overhead Triceps Extension',
              matchedExerciseId: null,
              sets: 4,
              targetReps: 10,
              suggestedReplacementId: 'exercise/$overhead_triceps_extension',
            },
            {
              name: 'Single Arm High Cable Lateral Raise',
              matchedExerciseId: 'exercise/$cable_lateral_raise',
              sets: 4,
              targetReps: 10,
            },
            {
              name: 'Cable Face Pull',
              matchedExerciseId: 'exercise/$face_pulls',
              sets: 4,
              targetReps: 10,
            },
            {
              name: 'Seated Machine Hip Abduction',
              matchedExerciseId: 'exercise/$machine_hip_abductions',
              sets: 4,
              targetReps: 10,
            },
            {
              name: '45° Leg Press Calf Raise',
              matchedExerciseId: null,
              sets: 4,
              targetReps: 10,
              suggestedReplacementId: 'exercise/$calf_raise',
            },
            {
              name: 'Seated Machine Hip Adduction',
              matchedExerciseId: null,
              sets: 4,
              targetReps: 10,
            },
            {
              name: 'Dumbbell Side Bend',
              matchedExerciseId: null,
              sets: 3,
              targetReps: 10,
            },
          ],
        },
        {
          name: 'Workout C',
          exercises: [
            {
              name: 'Pause Back Squat',
              matchedExerciseId: 'exercise/$barbell_back_squat',
              sets: 4,
              targetReps: 5,
            },
            {
              name: 'Conventional Deadlift',
              matchedExerciseId: 'exercise/$deadlift',
              sets: 4,
              targetReps: 5,
            },
            {
              name: '45° Incline Dumbbell Press',
              matchedExerciseId: null,
              sets: 4,
              targetReps: 15,
            },
            {
              name: 'Overhand Grip Plate-Loaded Machine Lat Pulldown',
              matchedExerciseId: 'exercise/$wide_grip_pulldown',
              sets: 3,
              targetReps: 15,
            },
            {
              name: 'Standing Dumbbell Lateral Raise',
              matchedExerciseId: null,
              sets: 3,
              targetReps: 10,
            },
            {
              name: 'Cable Rope Straight Arm Lat Pulldown',
              matchedExerciseId: 'exercise/$straight_arm_pulldown',
              sets: 3,
              targetReps: 10,
            },
            {
              name: 'Standing Dumbbell Biceps Curl',
              matchedExerciseId: 'exercise/$dumbbell_curl',
              sets: 3,
              targetReps: 10,
            },
            {
              name: 'Lying Hamstring Curl',
              matchedExerciseId: 'exercise/$lying_standing_leg_curl',
              sets: 3,
              targetReps: 10,
            },
            {
              name: 'Single Leg Leg Extension',
              matchedExerciseId: 'exercise/$unilateral_leg_extension',
              sets: 3,
              targetReps: 10,
            },
            {
              name: 'Cable Glute Kickback',
              matchedExerciseId: 'exercise/$donkey_kick',
              sets: 3,
              targetReps: 10,
            },
          ],
        },
      ],
      confidence: 0.95,
    },
  },
  {
    label: 'Program 2',
    workout: {
      workouts: [
        {
          name: 'Workout A',
          exercises: [
            {
              name: 'Low Incline Smith Machine',
              matchedExerciseId: null,
              sets: 4,
              targetReps: 5,
              suggestedReplacementId: 'exercise/$45_incline_bench_press_wide_grip',
            },
            {
              name: 'Bent-Over Barbell Row',
              matchedExerciseId: 'exercise/$bent_over_barbell_row',
              sets: 4,
              targetReps: 12,
            },
            {
              name: 'Plate Loaded Machine Shoulder Press',
              matchedExerciseId: null,
              sets: 4,
              targetReps: 11,
              suggestedReplacementId: 'exercise/$dumbell_overhead_press',
            },
            {
              name: '45° Leg Press',
              matchedExerciseId: 'exercise/$leg_press',
              sets: 4,
              targetReps: 11,
            },
            {
              name: 'Peck Deck Fly',
              matchedExerciseId: 'exercise/$pec_deck_fly',
              sets: 4,
              targetReps: 10,
            },
          ],
        },
        {
          name: 'Workout B',
          exercises: [
            {
              name: 'Seated Neutral Grip Dumbbell Overhead Press',
              matchedExerciseId: 'exercise/$dumbell_overhead_press',
              sets: 4,
              targetReps: 5,
            },
            {
              name: 'Cable Face Pull',
              matchedExerciseId: 'exercise/$face_pulls',
              sets: 4,
              targetReps: 10,
            },
            {
              name: 'Seated Machine Hip Adduction',
              matchedExerciseId: null,
              sets: 4,
              targetReps: 10,
            },
            {
              name: 'Barbell Bench Press',
              matchedExerciseId: 'exercise/$barbell_bench_press',
              sets: 4,
              targetReps: 9,
            },
          ],
        },
      ],
      confidence: 1,
    },
  },
];
